import { useEffect, useState } from "react";
import { Box, Flex, Heading, Text } from "@chakra-ui/react";
import { crm } from "../../theme";
import AddUsers from "./tabs/AddUsers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router-dom";
import { faHandshake, faUserPlus } from "@fortawesome/free-solid-svg-icons";
import Header from "../../components/Header/Header";
import { useMediaQuery } from "react-responsive";
import {
  FaAngleLeft,
  FaBoxOpen,
  FaCalendarAlt,
  FaClock,
  FaUniversity,
  FaFileExcel,
  FaFileInvoice,
  FaKey,
  FaPercent,
  FaListUl,
  FaShip,
  FaTruckLoading,
  FaUser,
  FaUserAlt,
  FaUsers,
} from "react-icons/fa";
import ClientRef from "./tabs/ClientRef";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import CompaniesList from "./tabs/Company/CompaniesList";
import AddSuppliers from "./tabs/AddSuppliers";
import Permissions from "./tabs/Permissions";
import CompanyPermissions from "./tabs/CompanyPermissions";
import Layout from "../../components/Layout";
import ExchangeConditions from "./tabs/ExchangeConditions";
import ProcessPartners from "./tabs/ProcessPartners";
import { ProcessPartnerType } from "../../models/ProcessPartner";
import TeamsManager from "./tabs/TeamsManager";
import TaxSettings from "./tabs/TaxSettings";
import NveSettings from "./tabs/NveSettings";
import RegistrationDefaults from "./tabs/RegistrationDefaults";
import SettlementSettings from "./tabs/SettlementSettings";
import FiscalIssuers from "./tabs/FiscalIssuers";
import Imports from "./tabs/Imports";
import StatusLimits from "./tabs/StatusLimits";
import UnitProfiles from "./tabs/UnitProfiles";
import BillingSettings from "./tabs/BillingSettings";
import { useCompany } from "../../contexts/CompanyContext";

const Configurations = () => {
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const auth = useAuthUser<any>();
  const navigate = useNavigate();
  const { currentCompany } = useCompany();
  const [currentTab, setCurrentTab] = useState<number | undefined>(undefined);
  const baseTabs = [
    {
      label: "Ref de cliente",
      icon: (
        <FaUserAlt color="var(--icon-color)" fontSize={isMobile ? 18 : 24} />
      ),
      content: <ClientRef />,
    },
    {
      label: "Gerenciar usuários",
      icon: (
        <FontAwesomeIcon
          color="var(--icon-color)"
          icon={faUserPlus}
          fontSize={isMobile ? 18 : 24}
        />
      ),
      content: <AddUsers />,
    },
    {
      label: "Gerenciar clientes",
      icon: (
        <FontAwesomeIcon
          color="var(--icon-color)"
          icon={faHandshake}
          fontSize={isMobile ? 18 : 24}
        />
      ),
      content: <CompaniesList />,
    },
    {
      label: "Adicionar fornecedores",
      icon: (
        <FaTruckLoading
          color="var(--icon-color)"
          fontSize={isMobile ? 18 : 24}
        />
      ),
      content: <AddSuppliers />,
    },
    {
      label: "Gerenciar permissões",
      icon: <FaKey color="var(--icon-color)" fontSize={isMobile ? 18 : 24} />,
      content: <Permissions />,
    },

    {
      label: "Gerenciar permissões das empresas",
      icon: (
        <Flex color="var(--icon-color)">
          <FaKey fontSize={isMobile ? 18 : 24} />
          <FaUser fontSize={isMobile ? 18 : 24} />
        </Flex>
      ),
      content: <CompanyPermissions />,
    },
    {
      label: "Condições de câmbio",
      icon: (
        <FaCalendarAlt
          color="var(--icon-color)"
          fontSize={isMobile ? 18 : 24}
        />
      ),
      content: <ExchangeConditions />,
    },
    {
      label: "Armadores",
      icon: (
        <FaShip color="var(--icon-color)" fontSize={isMobile ? 18 : 24} />
      ),
      content: (
        <ProcessPartners
          type={ProcessPartnerType.Shipowner}
          title="Armadores"
          description="Cadastre os armadores que aparecem no select do processo. O processo grava o nome escolhido."
          icon={<FaShip color="var(--icon-color)" />}
        />
      ),
    },
    {
      label: "Agentes de carga",
      icon: (
        <FaBoxOpen color="var(--icon-color)" fontSize={isMobile ? 18 : 24} />
      ),
      content: (
        <ProcessPartners
          type={ProcessPartnerType.FreightAgent}
          title="Agentes de carga"
          description="Cadastre os agentes de carga que aparecem no select do processo. O processo grava o nome escolhido."
          icon={<FaBoxOpen color="var(--icon-color)" />}
        />
      ),
    },
    {
      label: "Tributos de importação",
      icon: <FaPercent color="var(--icon-color)" fontSize={isMobile ? 18 : 24} />,
      content: <TaxSettings />,
    },
    {
      label: "Tabela NVE",
      icon: <FaListUl color="var(--icon-color)" fontSize={isMobile ? 18 : 24} />,
      content: <NveSettings />,
    },
    {
      label: "Registro DUIMP",
      icon: <FaListUl color="var(--icon-color)" fontSize={isMobile ? 18 : 24} />,
      content: <RegistrationDefaults />,
    },
    {
      label: "Prestação de contas",
      icon: <FaListUl color="var(--icon-color)" fontSize={isMobile ? 18 : 24} />,
      content: <SettlementSettings />,
    },
    {
      label: "Importações",
      icon: <FaFileExcel color="var(--icon-color)" fontSize={isMobile ? 18 : 24} />,
      content: <Imports />,
    },
    {
      label: "Limites de status",
      icon: <FaClock color="var(--icon-color)" fontSize={isMobile ? 18 : 24} />,
      content: <StatusLimits />,
    },
    {
      label: "Dados das unidades",
      icon: <FaUniversity color="var(--icon-color)" fontSize={isMobile ? 18 : 24} />,
      content: <UnitProfiles />,
    },
    {
      label: "Notas fiscais",
      icon: <FaFileInvoice color="var(--icon-color)" fontSize={isMobile ? 18 : 24} />,
      content: <FiscalIssuers />,
    },
    {
      label: "Parametrizações fiscais",
      icon: <FaPercent color="var(--icon-color)" fontSize={isMobile ? 18 : 24} />,
      content: <BillingSettings />,
    },
  ];

  const tabs =
    currentCompany === 2 || currentCompany === 5
      ? [
          ...baseTabs,
          {
            label: "Gerenciar equipes",
            icon: (
              <FaUsers color="var(--icon-color)" fontSize={isMobile ? 18 : 24} />
            ),
            content: <TeamsManager />,
          },
        ]
      : baseTabs;

  useEffect(() => {
    if (!auth?.admin) navigate("/");
  }, [auth]);

  useEffect(() => {
    if (currentTab !== undefined && currentTab >= tabs.length) {
      setCurrentTab(undefined);
    }
  }, [tabs.length, currentTab]);

  return (
    <Box height={isMobile ? undefined : "100vh"} overflow="hidden">
      <Layout>
        
        <Box
          height="95vh"
          overflowY="auto"
          padding="8px"
          backgroundColor="var(--main-bg-color)"
        >
          <Flex
            gap="8px"
            width="90%"
            cursor="pointer"
            mt="32px"
            ml="32px"
            alignItems="center"
            onClick={() =>
              isMobile
                ? currentTab === undefined
                  ? navigate(-1)
                  : setCurrentTab(undefined)
                : navigate(-1)
            }
          >
            <FaAngleLeft />
            <Heading fontWeight="normal" size="md">
              Voltar
            </Heading>
          </Flex>
          <Flex justifyContent="center">
            <Flex
              w={currentTab === undefined ? "45%" : "75%"}
              padding="32px"
              gap="32px"
              marginTop="32px"
              background="white"
              border={`1px solid ${crm.border}`}
              boxShadow={crm.shadowSm}
              borderRadius="12px"
            >
              {currentTab === undefined && (
                <Box width={"100%"}>
                  {tabs.map((tab, index) => (
                    <Flex
                      key={index}
                      backgroundColor={
                        index === currentTab
                          ? "var(--main-bg-color)"
                          : undefined
                      }
                      borderBottomWidth={
                        index === currentTab ? "3px" : undefined
                      }
                      borderBottomColor={
                        index === currentTab ? "var(--icon-color)" : undefined
                      }
                      _hover={{
                        backgroundColor:
                          index === currentTab
                            ? "var(--main-bg-color)"
                            : "var(--hover-color)",
                      }}
                      cursor="pointer"
                      padding="16px 8px"
                      gap="16px"
                      paddingLeft={isMobile ? 0 : "32px"}
                      marginRight={isMobile ? 0 : "16px"}
                      mb="8px"
                      alignItems="center"
                      borderRadius="12px"
                      border={`1px solid ${crm.border}`}
                      onClick={() => setCurrentTab(index)}
                    >
                      {tab.icon}
                      <Text
                        maxWidth="400px"
                        noOfLines={1}
                        overflow="hidden"
                        fontSize={isMobile ? "18px" : "24px"}
                      >
                        {tab.label}
                      </Text>
                    </Flex>
                  ))}
                </Box>
              )}
              {currentTab != undefined && tabs[currentTab] && (
                <Box p="32px" width="100%">
                  <Flex
                    gap="8px"
                    width="90%"
                    alignItems="center"
                    cursor="pointer"
                    onClick={() => setCurrentTab(undefined)}
                    mb="32px"
                  >
                    <FaAngleLeft />
                    <Heading fontWeight="normal" size="md">
                      Voltar para o menu
                    </Heading>
                  </Flex>
                  {tabs[currentTab].content}
                </Box>
              )}
            </Flex>
          </Flex>
        </Box>
      </Layout>
    </Box>
  );
};
export default Configurations;
